import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import Page from "./Page";
import AppFooter from "../components/footer/AppFooter";
import ReadOnlyTask from "../components/tasks/ReadOnlyTask";
import useCompletedTasksAPIClient from "../api/tasks/hooks/useCompletedTasksAPIClient";
import LoadingScreen from "../components/loading/LoadingScreen";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);

/**
 * Groups completed tasks by day based on their completion timestamp.
 * Tasks with a boolean `completed` value (legacy) are placed in a separate group.
 */
const groupTasksByDay = (tasks) => {
  const dayGroups = {};
  const legacyTasks = [];

  Object.values(tasks).forEach((task) => {
    if (typeof task.completed === "number") {
      const dayKey = dayjs(task.completed).format("YYYY-MM-DD");
      if (!dayGroups[dayKey]) {
        dayGroups[dayKey] = [];
      }
      dayGroups[dayKey].push(task);
    } else {
      // Legacy tasks with completed === true (boolean)
      legacyTasks.push(task);
    }
  });

  // Sort tasks within each day by completion time (most recent first)
  Object.keys(dayGroups).forEach((dayKey) => {
    dayGroups[dayKey].sort((a, b) => b.completed - a.completed);
  });

  // Sort day keys most recent first
  const sortedDayKeys = Object.keys(dayGroups).sort(
    (a, b) => dayjs(b).valueOf() - dayjs(a).valueOf(),
  );

  return { dayGroups, sortedDayKeys, legacyTasks };
};

const CompletedTasksStats = ({ completedTasks }) => {
  const stats = useMemo(() => {
    if (!completedTasks) {
      return { allTime: 0, today: 0, thisWeek: 0, thisMonth: 0 };
    }

    const now = dayjs();
    const startOfToday = now.startOf("day");
    const startOfWeek = now.startOf("isoWeek"); // Monday
    const startOfMonth = now.startOf("month");

    let allTime = 0;
    let today = 0;
    let thisWeek = 0;
    let thisMonth = 0;

    Object.values(completedTasks).forEach((task) => {
      if (task.completed) {
        allTime++;

        if (typeof task.completed === "number") {
          const completedTime = dayjs(task.completed);

          if (
            completedTime.isAfter(startOfToday) ||
            completedTime.isSame(startOfToday)
          ) {
            today++;
          }

          if (
            completedTime.isAfter(startOfWeek) ||
            completedTime.isSame(startOfWeek)
          ) {
            thisWeek++;
          }

          if (
            completedTime.isAfter(startOfMonth) ||
            completedTime.isSame(startOfMonth)
          ) {
            thisMonth++;
          }
        }
      }
    });

    return { allTime, today, thisWeek, thisMonth };
  }, [completedTasks]);

  return (
    <Box className="completed-tasks__stats">
      <Typography variant="h3" className="completed-tasks__stats-all-time">
        {stats.allTime} tasks completed
      </Typography>
      <Box className="completed-tasks__stats-breakdown">
        <Box className="completed-tasks__stats-item">
          <Typography variant="body2" className="completed-tasks__stats-value">
            {stats.today}
          </Typography>
          <Typography
            variant="caption"
            className="completed-tasks__stats-label"
          >
            Today
          </Typography>
        </Box>
        <Box className="completed-tasks__stats-item">
          <Typography variant="body2" className="completed-tasks__stats-value">
            {stats.thisWeek}
          </Typography>
          <Typography
            variant="caption"
            className="completed-tasks__stats-label"
          >
            This Week
          </Typography>
        </Box>
        <Box className="completed-tasks__stats-item">
          <Typography variant="body2" className="completed-tasks__stats-value">
            {stats.thisMonth}
          </Typography>
          <Typography
            variant="caption"
            className="completed-tasks__stats-label"
          >
            This Month
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const DaySummary = ({ date, tasks }) => {
  const taskCount = tasks.length;
  const dateObj = dayjs(date);
  const isToday = dateObj.isSame(dayjs(), "day");
  const isYesterday = dateObj.isSame(dayjs().subtract(1, "day"), "day");

  let dateLabel;
  if (isToday) {
    dateLabel = "Today";
  } else if (isYesterday) {
    dateLabel = "Yesterday";
  } else if (dateObj.isSame(dayjs(), "year")) {
    dateLabel = dateObj.format("dddd Do MMMM");
  } else {
    dateLabel = dateObj.format("dddd Do MMMM YYYY");
  }

  return (
    <Box className="completed-tasks__day-summary">
      <Typography
        variant="body1"
        className="completed-tasks__day-summary-label"
      >
        {dateLabel}
      </Typography>
      <Typography
        variant="body2"
        className="completed-tasks__day-summary-count"
      >
        {taskCount} task{taskCount !== 1 ? "s" : ""} completed
      </Typography>
    </Box>
  );
};

const DayDivider = ({ date, tasks }) => {
  return (
    <div className="completed-tasks__day-section">
      <div className="completed-tasks__day-divider">
        <div className="completed-tasks__day-divider-line" />
        <DaySummary date={date} tasks={tasks} />
        <div className="completed-tasks__day-divider-line" />
      </div>
      <div className="completed-tasks__task-list">
        {tasks.map((task) => (
          <ReadOnlyTask key={task.taskId} item={task} />
        ))}
      </div>
    </div>
  );
};

const CompletedTasksPage = () => {
  const authenticated = useSelector((state) => state.user.authenticated);
  const { getCompletedTasks } = useCompletedTasksAPIClient();
  const [completedTasks, setCompletedTasks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompletedTasks = async () => {
      try {
        const tasks = await getCompletedTasks();
        setCompletedTasks(tasks || {});
      } catch (error) {
        console.error("Failed to fetch completed tasks:", error);
        setCompletedTasks({});
      } finally {
        setLoading(false);
      }
    };

    if (authenticated) {
      fetchCompletedTasks();
    }
  }, [authenticated]);

  const { dayGroups, sortedDayKeys, legacyTasks } = useMemo(() => {
    if (!completedTasks) {
      return { dayGroups: {}, sortedDayKeys: [], legacyTasks: [] };
    }
    return groupTasksByDay(completedTasks);
  }, [completedTasks]);

  if (!authenticated) {
    return <LoadingScreen />;
  }

  if (loading) {
    return (
      <Page className="completed-tasks-page" pageKey="completed-tasks">
        <LoadingScreen />
      </Page>
    );
  }

  const hasNoTasks = sortedDayKeys.length === 0 && legacyTasks.length === 0;

  return (
    <Page className="completed-tasks-page" pageKey="completed-tasks">
      <Box className="completed-tasks__header">
        <EmojiEventsIcon className="completed-tasks__header-icon" />
        <Typography variant="h2" className="completed-tasks__title">
          Completed Tasks
        </Typography>
      </Box>

      <CompletedTasksStats completedTasks={completedTasks} />

      <Box className="completed-tasks__container">
        {hasNoTasks && (
          <Box className="completed-tasks__empty">
            <Typography variant="body1" className="completed-tasks__empty-text">
              No completed tasks yet. Complete some tasks and they'll show up
              here!
            </Typography>
          </Box>
        )}

        {sortedDayKeys.map((dayKey) => (
          <DayDivider key={dayKey} date={dayKey} tasks={dayGroups[dayKey]} />
        ))}

        {legacyTasks.length > 0 && (
          <div className="completed-tasks__day-section">
            <div className="completed-tasks__day-divider">
              <div className="completed-tasks__day-divider-line" />
              <Box className="completed-tasks__day-summary">
                <Typography
                  variant="body1"
                  className="completed-tasks__day-summary-label"
                >
                  Previously Completed
                </Typography>
                <Typography
                  variant="body2"
                  className="completed-tasks__day-summary-count"
                >
                  {legacyTasks.length} task
                  {legacyTasks.length !== 1 ? "s" : ""} completed
                </Typography>
              </Box>
              <div className="completed-tasks__day-divider-line" />
            </div>
            <div className="completed-tasks__task-list">
              {legacyTasks.map((task) => (
                <ReadOnlyTask key={task.taskId} item={task} />
              ))}
            </div>
          </div>
        )}
      </Box>
      <AppFooter page="completed-tasks" />
    </Page>
  );
};

export default CompletedTasksPage;
